import { App } from 'vue';

/**
 * Messages parent → iframe.
 */
export type ParentToChildMessage =
  /**
   * Parent → iframe handshake to establish the MessagePort.
   * Sent once, right after the iframe’s `load` event,
   * transferring port2 for two-way comms.
   */
  | { type: 'INIT_CHANNEL' }
  | { type: 'DISPLAY_VERSION'; data: { versionId: string; projectId: string } }
  | { type: 'SHIFT_SCHEDULE'; data: { date: string } }
  | {
      type: 'DISPLAY_TEMPLATE';
      data: { projectQueryId: string; templateId: string; tenantId: string };
    };

/**
 * Messages iframe → parent.
 */
export type ChildToParentMessage =
  | { type: 'SCHEDULE_LOADED' }
  | { type: 'SCHEDULE_SHIFTED'; data: { endDate: string } }
  | { type: 'VERSION_LOADED' }
  | { type: 'EXPORT_SCHEDULE'; data: { projectId: string; schedule: string | null | undefined } };

/**
 * Anything that can flow over our iframe channel.
 */
export type IframeChannelMessage = ParentToChildMessage | ChildToParentMessage;

/**
 * Handler for incoming ParentToChildMessage.
 */
export type ChannelMessageHandler = (msg: ParentToChildMessage) => void;

// symbol key for Vue’s provide/inject
const iframeChannelKey = Symbol('IframeChannel');

/**
 * Core channel abstraction.
 */
export class IframeChannel {
  private messagePort?: MessagePort;
  private handlers: ChannelMessageHandler[] = [];

  constructor() {
    // listen for the INIT_CHANNEL handshake
    window.addEventListener('message', (event) => {
      const incoming = event.data as Partial<ParentToChildMessage>;
      if (incoming?.type === 'INIT_CHANNEL' && event.ports.length) {
        const [port] = event.ports;
        this.messagePort = port;
        port.onmessage = (e) => {
          const msg = e.data as ParentToChildMessage;
          this.handlers.forEach((h) => h(msg));
        };
      }
    });
  }

  /** Subscribe to parent→iframe messages */
  public addHandler(fn: ChannelMessageHandler) {
    this.handlers.push(fn);
  }

  /** Unsubscribe */
  public removeHandler(fn: ChannelMessageHandler) {
    this.handlers = this.handlers.filter((h) => h !== fn);
  }

  /** Low-level send child→parent */
  private post(msg: ChildToParentMessage) {
    if (!this.messagePort) {
      // eslint-disable-next-line no-console -- Log other unhandled requests
      console.warn('[IframeChannel] no port; did you INIT_CHANNEL?');
      return;
    }
    this.messagePort.postMessage(msg);
  }

  /** Notify parent that schedule is loaded */
  public sendScheduleLoaded() {
    this.post({ type: 'SCHEDULE_LOADED' });
  }

  /** Notify parent that schedule was shifted */
  public sendScheduleShifted(endDate: Date | null) {
    this.post({ type: 'SCHEDULE_SHIFTED', data: { endDate: endDate?.toISOString() ?? '' } });
  }

  /** Notify parent that version is loaded */
  public sendVersionLoaded() {
    this.post({ type: 'VERSION_LOADED' });
  }

  /** Request parent to export a schedule */
  public sendExportSchedule(data: { projectId: string; schedule: string | null | undefined }) {
    this.post({ type: 'EXPORT_SCHEDULE', data });
  }
}

export function installIframeChannel(app: App) {
  const channel = new IframeChannel();
  app.provide(iframeChannelKey, channel);
  return channel;
}

export function useProvidedIframeChannel(): IframeChannel {
  return inject(iframeChannelKey) as IframeChannel;
}

export function useChannelMessageHandler(handler: ChannelMessageHandler) {
  const channel = useProvidedIframeChannel();

  onMounted(() => channel.addHandler(handler));
  onUnmounted(() => channel.removeHandler(handler));

  return { channel };
}
